import { env } from "./env";

export type Result<T> = { ok: true; result: T } | { ok: false; error: string };

async function call<T>(token: string, method: string, path: string, body?: unknown): Promise<Result<T>> {
  try {
    const res = await fetch(`${env.apiUrl}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    if (!res.ok) {
      let msg = text;
      try {
        const j = JSON.parse(text);
        msg = typeof j.error === "string" ? j.error : JSON.stringify(j.error);
      } catch {
        /* keep text */
      }
      return { ok: false, error: `${res.status}: ${msg}` };
    }
    return { ok: true, result: (text ? JSON.parse(text) : undefined) as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

export const http = {
  get: <T>(t: string, p: string) => call<T>(t, "GET", p),
  post: <T>(t: string, p: string, b: unknown = {}) => call<T>(t, "POST", p, b),
  patch: <T>(t: string, p: string, b: unknown) => call<T>(t, "PATCH", p, b),
  del: <T>(t: string, p: string) => call<T>(t, "DELETE", p),
};

/** Fetch a protected binary (e.g. a rendered still) and return an object URL. */
export async function blobUrl(token: string, path: string): Promise<string | null> {
  const r = await fetch(`${env.apiUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) return null;
  return URL.createObjectURL(await r.blob());
}

export interface Me {
  id: string;
  sub: string;
  email: string | null;
  role: "admin" | "reader" | "tech" | "vendor";
  tenantId: string | null;
  isOps?: boolean;
  tenant?: { code: string; name: string };
  features?: { remoteSessions: boolean };
}
export interface Patient {
  id: string;
  mrn: string;
  issuer: string;
  familyName: string;
  givenName: string;
  birthDate: string | null;
  sex: string | null;
}
export interface Order {
  id: string;
  accession: string;
  studyInstanceUid: string;
  status: string;
  modality: string;
  stationAeTitle: string;
  scheduledAt: string;
  procedureDescription: string;
  requesterName: string | null;
  sourceSystem: string;
  patient: Patient;
  tenant: { code: string };
  _count?: { instances: number; recordings: number; sessions?: number };
}
export interface Instance {
  id: string;
  sopInstanceUid: string;
  sopClassUid: string;
  sopClassName: string;
  storage: string;
  sizeBytes: number;
  fromAet: string | null;
  receivedAt: string;
  validation: { ok: boolean; missing: string[]; warnings: string[]; profile: string };
  /** Eye imaged: R, L or B; null when not recorded. Source "device" (from the object) or "user" (assigned in Jupiter). */
  laterality: "R" | "L" | "B" | null;
  lateralitySource: "device" | "user" | null;
  lateralitySetBy: string | null;
  lateralitySetAt: string | null;
  /** Jupiter shows a reference preview for photographs only. */
  previewable?: boolean;
  /** The instrument vendor's review software, used for diagnosis. */
  viewer?: { name: string; url: string } | null;
}
export interface Recording {
  id: string;
  source: string;
  s3Key: string;
  sizeBytes: number;
  startedAt: string;
}
export interface OrderSession {
  id: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
  mergeStatus: string | null;
  mergeError: string | null;
  node: { aet: string; displayName: string | null };
  participants: SessionParticipantRecord[];
}
/** Who took part, from user records: an account, or a QR-joined device attributed to the staff member who admitted it. */
export interface ParticipantIdentity {
  via: "account" | "pass";
  name: string;
  email: string | null;
  role: string | null;
  issuedBy?: { name: string; email: string | null; role: string | null };
}
export interface SessionParticipantRecord {
  id: string;
  kind: string;
  label: string;
  device: string | null;
  deviceName?: string;
  joinedAt: string;
  leftAt: string | null;
  removedAt: string | null;
  identity: ParticipantIdentity | null;
}
export interface OrderDetail extends Order {
  instances: Instance[];
  recordings: (Recording & { sessionId?: string | null })[];
  sessions: OrderSession[];
}
export interface StreamInfo {
  deviceId: string;
  nodeId?: string;
  whepUrl: string;
  turn: { urls: string[]; username: string; credential: string; ttl: number };
}
export interface DicomNode {
  id: string;
  aet: string;
  host: string | null;
  port: number | null;
  status: string;
  displayName: string | null;
  manufacturer: string | null;
  model: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  lastSeenKind: string | null;
  seenCount: number;
  lastEcho: { ok: boolean; detail?: string; at: string } | null;
  echoRequestedAt: string | null;
  suggestedModule?: string | null;
  /** Provides live video: a module with a live transport whose live add-on is switched on. Remote sessions need it. */
  live?: boolean;
  /** Connection test (POST /admin/nodes/:id/diagnose); absent on servers that do not have it yet. */
  diagnoseRequestedAt?: string | null;
  lastDiagnostic?: NodeDiagnostic | null;
  device: { id?: string; name: string; siteCode: string; tenant: { code: string }; lastSeenAt?: string | null };
  modules: { id?: string; moduleId: string; status?: string; settings?: Record<string, unknown> }[];
}
export interface DiagnosticStep {
  key: string;
  label: string;
  status: "pass" | "warn" | "fail" | "skip";
  detail: string;
  fix?: string;
  data?: Record<string, string>; // what the device said: SDP, reply headers, packets received
}
export interface NodeDiagnostic {
  at: string;
  ok: boolean;
  summary: string;
  steps: DiagnosticStep[];
  stream?: { codec?: string; width?: number; height?: number; fps?: number; bitrateKbps?: number };
}
export interface ModuleSetting {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  default?: string | number | boolean;
  help?: string;
  options?: { value: string; label: string }[]; // type "enum"
}
export interface ModuleRow {
  id: string;
  version: string;
  vendor: string;
  name: string;
  manifest: {
    name: string;
    device: { model: string; modality: string };
    dicom: Record<string, unknown>;
    live?: unknown;
    settings: ModuleSetting[];
  };
}
export interface Site {
  id: string;
  code: string;
  name: string;
  address: string | null;
  timezone?: string;
  updateWindow?: string;
  tenant: { code: string };
  devices: { id: string; name: string; lastSeenAt: string | null; revokedAt: string | null }[];
}
export interface Edge {
  id: string;
  name: string;
  siteCode: string;
  hostname: string | null;
  arch: string | null;
  agentVersion: string | null;
  edgeAeTitle: string;
  dicomPort: number;
  lastSeenAt: string | null;
  lastHealth: { status?: string; detail?: Record<string, unknown> } | null;
  revokedAt: string | null;
  online: boolean;
  tenant: { code: string };
  site: { code: string; name: string } | null;
  _count: { nodes: number; instances: number };
}
export interface Tenant {
  id: string;
  code: string;
  name: string;
  healthimagingDatastoreId: string | null;
  moyaePracticeId: number | null;
  defaultRecordingQuality: string;
}
export const RECORDING_QUALITIES = [
  { value: "economy", label: "Economy (480p, everyday)" },
  { value: "standard", label: "Standard (720p)" },
  { value: "full", label: "Full (1080p, demos)" },
  { value: "audio", label: "Audio only" },
];
export interface UserRow {
  id: string;
  sub: string;
  email: string | null;
  name: string | null;
  role: string;
  lastSeenAt: string | null;
  tenant: { code: string } | null;
}
export interface AuditRow {
  id: string;
  at: string;
  actor: string;
  actorType: string;
  action: string;
  target: string | null;
  ip: string | null;
  userAgent: string | null;
  outcome: string;
  detail: unknown;
}
export interface Stats {
  orders: { status: string; _count: number }[];
  instances: { storage: string; _count: number }[];
  recordings: number;
  edgesTotal: number;
  edgesOnline: number;
  nodesPending: number;
  peopleServed: number;
  archivedBytes: number;
}

/* ───── Implementer console (docs/IMPLEMENTER_CONSOLE.md) ───── */

/** True when a failed call means the endpoint is not deployed on this server yet. */
export const isNotFound = (error: string) => error.startsWith("404");

/** Field errors from a 400 carrying a zod `flatten()` body, keyed by field. */
export function fieldErrors(error: string): Record<string, string> {
  const m = /^400: (.*)$/s.exec(error);
  if (!m) return {};
  try {
    const j = JSON.parse(m[1]) as { fieldErrors?: Record<string, string[] | string> };
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(j.fieldErrors ?? {})) out[k] = Array.isArray(v) ? v.join("; ") : String(v);
    return out;
  } catch {
    return {};
  }
}

/** DICOM AE title (SH): letters, digits, underscore, dash, dot, up to 16. */
export const AE_PATTERN = /^[A-Za-z0-9_.-]{1,16}$/;

export type LogLevel = "default" | "verbose" | "trace";
export interface EdgeSettings {
  edgeAeTitle: string;
  dicomPort: number;
  dicomTlsEnabled: boolean;
  dicomTlsPort: number;
  tlsRequireClientCert: boolean;
  logLevel: LogLevel;
}
export interface TlsCertificate {
  fingerprint: string;
  subject: string;
  notAfter: string;
}
export interface EdgeHealth {
  /** Instrument streams the box pulls (agent 0.6.6 and later report kbps). */
  streams?: { name: string; ready: boolean; kbps: number | null }[];
  reportedAt?: string | null;
  lanAddresses: string[];
  versions: { agent?: string; orthanc?: string; mediamtx?: string };
  clockSkewMs: number | null;
  listening: { dicom: number; dicomTls: number | null };
  components: Record<string, string>;
  /** Local disk and upload backlog (agent 0.4.0 and later). */
  storage?: {
    diskFreeBytes: number | null;
    diskTotalBytes: number | null;
    orthancBytes: number | null;
    orthancInstances: number | null;
    uploadsInProgress: number;
    archivedAwaitingCleanup: number;
    lastUploadAt: string | null;
    lastUploadError: string | null;
    retentionDays: number;
    directToS3AboveBytes: number;
  } | null;
}
export interface EdgeDetail {
  id: string;
  name: string;
  siteCode: string;
  tenant: { code: string };
  site: { code: string; name: string } | null;
  hostname: string | null;
  arch: string | null;
  agentVersion: string | null;
  lastSeenAt: string | null;
  online: boolean;
  revokedAt: string | null;
  settings: EdgeSettings;
  tls: TlsCertificate | null;
  health: EdgeHealth | null;
  pendingActions: { id: string; action: string; requestedAt: string }[];
  installCommand: string;
  upgradeCommand: string;
  updates?: EdgeUpdates;
}
export type Channel = "canary" | "early" | "stable";
export const CHANNELS: Channel[] = ["canary", "early", "stable"];
export interface EdgeUpdates {
  channel: Channel;
  channelOverride: Channel | null;
  release: string | null;
  target: string | null;
  status: "updating" | "verifying" | "ok" | "failed" | "rolled-back" | null;
  startedAt: string | null;
  finishedAt: string | null;
  detail: string | null;
  pausedUntil: string | null;
  pausedReason: string | null;
  updateNowAt: string | null;
  window: string;
}
export interface Fleet {
  channels: {
    channel: Channel;
    stage: number;
    stageLabel: string;
    stageSince: string;
    haltedAt: string | null;
    haltReason: string | null;
    release: { id: string; version: string; notes: string | null; emergency: boolean; createdAt: string };
  }[];
  releases: {
    id: string;
    version: string;
    notes: string | null;
    emergency: boolean;
    createdAt: string;
    images: Record<string, string>;
  }[];
  devices: {
    id: string;
    name: string;
    tenant: string;
    site: string;
    channel: Channel;
    channelOverride: Channel | null;
    online: boolean;
    agentVersion: string | null;
    release: string | null;
    target: string | null;
    updateStatus: string | null;
    updateStartedAt: string | null;
    updateFinishedAt: string | null;
    updateDetail: string | null;
    updatesPausedUntil: string | null;
    updatesPausedReason: string | null;
    updateNowAt: string | null;
    window: string;
    stuck: boolean;
  }[];
}
export type EdgeAction = "restart-dicom" | "reapply-config" | "collect-logs";

export interface ModuleSchemaField {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  default?: unknown;
  help?: string;
  provisional?: boolean;
  section?: string;
  options?: { value: string; label: string }[]; // type "enum"
}
export interface ModuleInstall {
  installId: string;
  moduleId: string;
  name: string;
  status: string;
  settings: Record<string, string | number | boolean>;
  secretsSet: string[];
  schema: ModuleSchemaField[];
}
export interface NodeNetwork {
  host: string | null;
  port: number | null;
  dicomTls: boolean;
  clientCert: { fingerprint: string; subject: string } | null;
  worklistEnabled: boolean;
  storageEnabled: boolean;
}
export interface NodeDetail {
  id: string;
  deviceId: string;
  aet: string;
  displayName: string | null;
  status: string;
  manufacturer: string | null;
  model: string | null;
  /** Not in the contract; shown in the header when the server includes it. */
  device?: {
    id?: string;
    name: string;
    siteCode: string;
    tenant?: { code: string };
    lastSeenAt?: string | null;
    online?: boolean;
  };
  network: NodeNetwork;
  connectTo: { host: string | null; port: number; aet: string; tls: boolean };
  modules: ModuleInstall[];
  lastEcho: { ok: boolean; detail?: string; at: string } | null;
  lastDiagnostic: (NodeDiagnostic & { observed?: { fingerprint?: string } }) | null;
  diagnoseRequestedAt?: string | null;
  echoRequestedAt?: string | null;
  activity: {
    lastWorklistQueryAt: string | null;
    lastStoreAt: string | null;
    lastStoreStatus: string | null;
    storesToday: number;
  };
  /** Objects received from this instrument, by type, with how often the instrument recorded the eye. */
  received?: {
    sopClassUid: string;
    name: string;
    objects: number;
    eyeFromInstrument: number;
    eyeAssigned: number;
    lastReceivedAt: string | null;
  }[];
}
export interface NodePatchBody {
  displayName?: string;
  aet?: string;
  host?: string | null;
  port?: number | null;
  dicomTls?: boolean;
  clientCertPem?: string | null;
  worklistEnabled?: boolean;
  storageEnabled?: boolean;
}
export interface WorklistPreviewRow {
  accession: string;
  patientName: string;
  mrn: string;
  scheduledAt: string;
  procedure: string;
  stationAeTitle: string;
  sharedWithAll: boolean;
}
export interface SetupSheet {
  title: string;
  intro: string;
  sections: { heading: string; steps: string[] }[];
  values: Record<string, string>;
}

export type EventSource = "dicom" | "tls" | "stream" | "agent" | "gate";
export type EventLevel = "info" | "warn" | "error";
export interface JupiterEvent {
  id: string;
  at: string;
  deviceId: string;
  nodeId: string | null;
  aet: string | null;
  ip: string | null;
  source: EventSource;
  level: EventLevel;
  kind: string;
  message: string;
  raw: string | null;
}
export interface EventPage {
  items: JupiterEvent[];
  nextAfter: string | null;
}

/** Fetch a protected text or binary file and hand it to the browser as a download. */
export async function downloadFile(token: string, path: string, filename: string): Promise<string | null> {
  try {
    const r = await fetch(`${env.apiUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return `${r.status}: ${(await r.text()) || r.statusText}`;
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}
